/**
 * useFoundationalTour — auto-starts the foundational tour for new users.
 *
 * Gating query: only start the tour if the current user has NO row in
 * public.user_onboarding for (user_id = auth.uid(), tour_id = FOUNDATIONAL_TOUR_ID,
 * version = FOUNDATIONAL_TOUR_VERSION) with status IN ('completed',
 * 'dismissed_permanent'). The version constant is read from the tour module so
 * that bumping the tour version (e.g., v1 → v2 to re-prompt after a re-scope)
 * automatically widens the gating window without a code change here.
 *
 * Mount this hook once, in DashboardLayout, after the auth + workspace
 * providers. It runs once per session — re-prompt logic for 'dismissed' or
 * 'skipped' users falls out for free since they re-enter the dashboard on
 * the next session.
 */

import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useTour } from './TourProvider';
import { DontShowAgainCheckbox } from './DontShowAgainCheckbox';
import {
  FOUNDATIONAL_TOUR_ID,
  FOUNDATIONAL_TOUR_VERSION,
  buildFoundationalTour,
} from '@/lib/tours/foundational-v2';

const TERMINAL_STATUSES = ['completed', 'dismissed_permanent'];

export function useFoundationalTour(): void {
  const { user, loading: authLoading } = useAuth();
  const { startTour, isRunning } = useTour();
  // Belt-and-suspenders: ensure we only attempt the gating query once per
  // mount, even if the auth state flickers.
  const checkedRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    if (isRunning) return;
    if (checkedRef.current) return;
    checkedRef.current = true;

    let cancelled = false;

    async function maybeStart(userId: string) {
      try {
        const { data, error } = await supabase
          .from('user_onboarding')
          .select('status')
          .eq('user_id', userId)
          .eq('tour_id', FOUNDATIONAL_TOUR_ID)
          .eq('version', FOUNDATIONAL_TOUR_VERSION)
          .maybeSingle();

        if (cancelled) return;
        if (error) {
          // Don't trigger the tour on a failed gating query — better to
          // miss showing the tour than to spam every page load.
          if (import.meta.env.DEV) {
            console.warn(
              '[useFoundationalTour] gating query failed; not starting tour',
              error,
            );
          }
          return;
        }

        // Row exists with terminal status → user already finished or
        // permanently dismissed. Do not start.
        if (data && TERMINAL_STATUSES.includes(data.status)) return;

        // No row, or non-terminal row ('dismissed' / 'skipped'). Start.
        const finalContent = (
          <div>
            You&apos;re set. Want a refresher? Restart the tour anytime from
            the Help menu.
            <DontShowAgainCheckbox />
          </div>
        );
        const steps = buildFoundationalTour(finalContent);
        startTour(
          FOUNDATIONAL_TOUR_ID,
          FOUNDATIONAL_TOUR_VERSION,
          steps,
        );
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn(
            '[useFoundationalTour] unexpected error; not starting tour',
            err,
          );
        }
      }
    }

    void maybeStart(user.id);

    return () => {
      cancelled = true;
    };
  }, [user, authLoading, isRunning, startTour]);
}
